import moment from 'moment';
import { bearing } from '@turf/turf';
import { point } from '@turf/helpers';

import { Info } from './Info.js';
/*
 This function returns a data object like so:

{
  vessel: {
    shipName: "Sliver",
    data: [
      {t:obj:moment, twa: 89, tws: 12.1, bs: 6.5, lat: 41.19, lon: 174.50, sog: 5.87, th: 321.3},
    ]
  },
  ais:[
      shipName: "Sliver",
      mmsi: 123456789,
      data: [
        {t:obj:moment, lat: 41.19, lon: 174.50, sog: 5.87, th: 321.3},
      ]
    ],
    923456789: { ... }
  }
}
 */

/*
TODO
  - add sea talk parsing https://github.com/MartinDavidWaller/SeaTalkNMEA/blob/main/Source/SeaTalkNMEA/SeaTalkNMEA.ino
     http://www.thomasknauf.de/rap/seatalk2.htm
*/

export function importJSON(json) {
    const data = JSON.parse(json);
    if (data != null && data != undefined) {
        return cleanAndAddCalculatedFields(data);
    }
    return null;
}

export function importRaceInfoJSON(json) {
    const data = JSON.parse(json);
    return cleanRaceInfoData(data);
}

export async function parseNmeaData(text, notifyFn = null) {
    let timeFrames;
    let aisTracks;
    [timeFrames, aisTracks] = await _parseNmeaDataToTimeframes(text, notifyFn);
    timeFrames = rollingAverage(timeFrames, 1, 3);
    const records = findRecords(timeFrames);
    
    const aisArr = [];
    for(const mmsi in aisTracks) {
        const track = aisTracks[mmsi];
        const aisRecords = findRecords(track.data);
        track.records = aisRecords;
        aisArr.push(track);
    }
    aisArr.sort((a,b) => b.data.length - a.data.length);
    
    const data = {
        vessel: {
            shipName: "",
            data: timeFrames,
            records: records
        },
        ais: aisArr
    };
    return cleanAndAddCalculatedFields(data);
}

export async function _parseNmeaDataToTimeframes(text, notifyFn = null) {
    const lines = text.split('\n');
    let timeFrames = [];
    let oldTime = null;
    let currentRawFrame = createEmptyRawFrame();
    let aisTracks = {};
    const optionalDataFunctions = dataFunctionList(lines);
    let startTime = null;
    let oldHour = 0;
    for (const line of lines) {
        const trimmedLine = line.trim();
        if (!validateChecksum(line)) {
            console.log("invalid checksum NMEA data:",line);
            continue;
        }
        const newTime = storePosition(trimmedLine, currentRawFrame, oldTime);
        if (newTime != null) {
            if (currentRawFrame.t == null) {
                currentRawFrame.t = newTime;
            } else if (newTime != null && newTime.isAfter(currentRawFrame.t)){
                timeFrames.push(...createTimeFrames(currentRawFrame, newTime))
                currentRawFrame = createEmptyRawFrame();
            }
            oldTime = newTime;
            const newHour = Math.floor(newTime.valueOf() / 3600000);
            if (newHour != oldHour) {
                startTime = startTime == null ? newTime : startTime;
                const hours = newTime.diff(startTime, 'hours') ;
                if (!Number.isNaN(hours)) {
                    if (notifyFn != null)
                        await notifyFn('Hours on the rail: ' + hours);
                    oldHour = newHour;
                }
            }
        }
        // }
        try {
            storeAisSentence(trimmedLine, aisTracks, oldTime);
            storeSpeedFromVHW(trimmedLine, currentRawFrame);
            storeWindFromMWV(trimmedLine, currentRawFrame);
            // storeHeadingFromHDT(trimmedLine, currentRawFrame);
            storeOrientationFromXDR(trimmedLine, currentRawFrame);
            for(const fn of optionalDataFunctions) {
                fn(trimmedLine, currentRawFrame);
            }
        } catch (e) {
        console.warn("Error parsing line:", trimmedLine, e);
        } 
    }
    // console.log(summarizeAisTracks(aisTracks));
    // console.log("frames:", timeFrames);
    timeFrames = removeImpossibleData(timeFrames);
    // console.log(timeFrames);
    return [timeFrames, aisTracks];
}

function isInvalid(frame, field) {
    if (frame != null) {
        return frame[field] == undefined && frame[field] == null;
    }
    return true;
}
function maxAbs(frame, oldFrame, field) {
    if (isInvalid(oldFrame,field))
        return isInvalid(frame,field) ? null : frame;
    else if (!isInvalid(frame, field) && Math.abs(frame[field]) > Math.abs(oldFrame[field]))
        return frame;
    else 
        return oldFrame;
}
function findRecords(frames) {
    if (frames != null && frames.length > 0) {
        let maxBsFrame = null;
        let maxSogFrame = null;
        let maxRollFrame = null;
        let maxWindSpdFrame = null;
        frames.forEach(f => {
            maxBsFrame = maxAbs(f, maxBsFrame, 'bs');
            maxSogFrame = maxAbs(f, maxSogFrame, 'sog');
            maxRollFrame = maxAbs(f, maxRollFrame, 'p');
            maxWindSpdFrame = maxAbs(f, maxWindSpdFrame, 'tws');
        });
        return {
            maxBs: maxBsFrame,
            maxSog: maxSogFrame,
            maxR: maxRollFrame,
            maxTws: maxWindSpdFrame,
        };
    }
    return {};
}

// ############################################### utilities ###################################################
export function convert360North0To180PosNeg(angleDeg) {
    if (angleDeg > 180) {
        angleDeg = (angleDeg%360 - 180) - 180;
    }
    return parseInt(angleDeg);
}

export function getBearingLonLat(lon1, lat1, lon2, lat2) {
    const b = bearing(point([lon1, lat1]), point([lon2, lat2]));
    return (b + 360) % 360;
}

export function getBearingFrames(f1, f2) {
    return getBearingLonLat(f1.lon, f1.lat, f2.lon, f2.lat);
}

export function bearingDelta(from, to, accessFn = (e) => {return e;}) {
    return (accessFn(to) - accessFn(from) + 540) % 360 - 180;
}

//average by extracting deltas.
export function averageAngle(angles, accessFn = (e) => {return e;}) {
    if (angles == null) return NaN;
    if (angles.length === 0) return NaN;
    if (angles.length === 1) return angles[0];
    angles = angles.filter(a => Number.isFinite(accessFn(a)));
    if (angles.length > 0) {
        const from = angles[0];
        let deltas = angles.map(to => bearingDelta(from, to, accessFn));
        const avgDelta = deltas.reduce((acc, b) => acc + b) / deltas.length;
        let avg = accessFn(from) + avgDelta;
        avg = (avg % 360 + 360) % 360;
        return parseInt(avg);
    } else {
        return NaN;
    }
}

export function averageValue(values, dp, accessFn = (e) => {return e;}) {
    if (!values || values.length === 0) return null;
    let avg = 0;
    let len = values.length;
    for(let i=0;i<values.length;i++) {
        const val = parseFloat(accessFn(values[i]));
        if (Number.isFinite(val)) {
            avg+= val;
        } else {
            len--;
        }
    }
    avg = avg / len;
    if (dp != null) {
        avg = parseFloat(avg.toFixed(dp));
    }
    return avg;
}

export function calculateTrueWind(boatSpeed, awaDeg, aws) {
    const awaRad = awaDeg * Math.PI / 180;
    const wx = aws * Math.sin(awaRad);
    const wy = aws * Math.cos(awaRad);

    // True wind vector = apparent wind vector + (-boat speed vector)
    const twx = wx;
    const twy = wy - boatSpeed;

    // True wind speed and angle (relative to bow)
    const tws = Math.sqrt(twx * twx + twy * twy);
    let twa = Math.atan2(twx, twy) * 180 / Math.PI;
    if (twa < 0) twa += 360;

    return {
        tws: parseFloat(tws.toFixed(2)),
        twa: parseFloat(twa.toFixed(0))
    };
}

export function projectVector(angleDeg, distance, projectAngleDeg) {
    const angleDiff = ((angleDeg - projectAngleDeg + 540) % 360) - 180; // Range [-180, 180]
    const projected = distance * Math.cos(angleDiff * Math.PI / 180);
    return projected;
}

// ############################################### end utilities ###################################################



function cleanAndAddCalculatedFields(data) {
    if (data == null || data.vessel == null || data.vessel.data == null) {
        alert('bad data file!');
        return null;
    }
    for(let i=data.vessel.data.length-1;i>=0;i--){
        const f = data.vessel.data[i];
        if (!cleanFrame(f)){
            data.vessel.datas.splice(i,1);
            continue;
        }
        if (i > 0) {
            f._cog = parseInt(getBearingFrames(data.vessel.data[i-1],f));
            if (f.twa == null || isNaN(f.twa)) {
                const tw = calculateTrueWind(f.bs, f.awa, f.aws);
                f.twa = tw.twa;
                f.tws = tw.tws;
            }
            if (f.twa != null && f.twa != undefined) {
                f._twd = parseInt((f._cog + f.twa) % 360);
                f._awd = parseInt((f._cog + f.awa) % 360);
                f._vmg = parseFloat(projectVector(f._cog, f.bs, f._twd).toFixed(2));
                const windCourseDiffDeg = Math.abs(bearingDelta(f._twd, f._cog));
                if (windCourseDiffDeg > 90) {
                    f._vmg = -1 * f._vmg;
                }
            }
        }
        if (data.vessel.records) {
            cleanFrame(data.vessel.records.maxBs);
            cleanFrame(data.vessel.records.maxSog);
            cleanFrame(data.vessel.records.maxR);
            cleanFrame(data.vessel.records.maxTws);
        }
    };
    if (data.ais) {
        for(const aisData of data.ais) {
            if (aisData.data != undefined && aisData.data.length > 0) {
                for(let i=aisData.data.length-1;i>=0;i--){
                    const f = aisData.data[i];
                    if (!cleanFrame(f)) {
                        aisData.data.splice(i,1);
                        continue;
                    }
                    if (f.sog > 50 || Math.abs(f.lat) > 90 || Math.abs(f.lon) > 180 ) {
                        console.log("removed impossible ais data:",f)
                        aisData.data.splice(i,1);
                        continue;
                    }
                    if (i > 0) {
                        f._cog = getBearingFrames(aisData.data[i-1],f);
                    }
                    cleanFrame(aisData.records.maxSog);
                };
            }
        }
    }
    // console.log("data:", data);
    return data;
}

function cleanRaceInfoData(data) {
    if (data == null) {
        return Info.getEmptyRaceInfo();
    }
    if (data.annotations != undefined && data.annotations != null) {
        for (let i = data.annotations.length - 1; i >= 0; i--) {
            const f = data.annotations[i];
            const begin_ts = moment(f.begin_ts);
            const end_ts = moment(f.end_ts);
            if (begin_ts.isValid() && end_ts.isValid()) {
                f.begin_ts = begin_ts;
                f.end_ts = end_ts;
                if (f.id == undefined || f.id == null) {
                    f.id = i;
                }
            } else {
                console.log("invalid data in annotations", f);
                data.annotations.splice(i, 1);
            }
        }
    } else {
        data.annotations = [];
    }
    console.log("data:", data);
    return data;
}

function cleanFrame(f) {
    if (f == null)
        return false;
    f.t = moment(f.t);
    if (!f.t.isValid()) {
        console.error("invalid data in frame", f);
        return false;
    }
    if (f.lat === undefined || f.lat == null || Number.isNaN(f.lat)) {
        console.error("problem with lat", f);
        return false;
    }
    if (f.lon === undefined || f.lon == null || Number.isNaN(f.lon)) {
        console.error("problem with lon", f);
        return false;
    }

    cleanDataPoint(f, 'twa');
    cleanDataPoint(f, 'tws');
    cleanDataPoint(f, 'sog');
    cleanDataPoint(f, 'cog');
    cleanDataPoint(f, 'bs');
    cleanDataPoint(f, 'th');
    cleanDataPoint(f, 'awa');
    cleanDataPoint(f, 'aws');
    cleanDataPoint(f, 'p');
    cleanDataPoint(f, 'r');
    return true;
}

function cleanDataPoint(f,field) {
    if (f[field] !== undefined || f[field] != null) {
        f[field] = parseFloat(f[field]);
    }
}

/**
 * Interpolates AIS data for each ship in the ais array at 1-second intervals.
 * For each ship, replaces its data array with interpolated points for t, lat, lon, sog, th.
 * @param {Object} data - The main data object containing ais array.
 */
export function interpolateAIS(data) {
    if (!data || !Array.isArray(data.ais)) return;

    data.ais.forEach(ship => {
        const original = ship.data;
        if (!original || original.length < 2) return;

        let interpolated = [];
        for (let i = 0; i < original.length - 1; i++) {
            const a = original[i];
            const b = original[i + 1];

            // Ensure valid times
            const tA = typeof a.t === 'object' && a.t.isValid ? a.t : moment(a.t);
            const tB = typeof b.t === 'object' && b.t.isValid ? b.t : moment(b.t);
            const dt = tB.diff(tA, 'seconds');

            if (dt < 1) continue;

            for (let s = 0; s < dt; s++) {
                const frac = s / dt;
                if (s === 0) {
                    interpolated.push(a);
                } else {
                    const lon = a.lon + frac * (b.lon - a.lon);
                    const lat = a.lat + frac * (b.lat - a.lat);
                    const last = interpolated[interpolated.length-1];
                    const _cog = getBearingLonLat(last.lon, last.lat, lon, lat);
                    interpolated.push({
                        t: tA.clone().add(s, 'seconds'),
                        lat: lat,
                        lon: lon,
                        sog: a.sog + frac * (b.sog - a.sog),
                        cog: a.cog + frac * (bearingDelta(a.cog,b.cog)) % 360,                        
                        _cog: _cog,                        
                        th: a.th,
                        intrp: true
                    });
                }
            }
        }
        interpolated.push(original[original.length - 1]);
        ship.data = interpolated;
    });
    return;
}

/**
 * Interpolates AIS data for each ship in the ais array at 1-second intervals.
 * For each ship, replaces its data array with interpolated points for t, lat, lon, sog, th.
 * @param {Object} data - The main data object containing ais array.
 */
export function pruneInterpolatedAIS(data) {
    if (!data || !Array.isArray(data.ais)) return;

    data.ais.forEach(ship => {
        const original = ship.data;
        if (!original || original.length < 2) return;
        for (let i = original.length-1; i >=0 ; i--) {
            if (original[i].intrp == true) {
                original.splice(i,1);
            }
        }
    });
}





/*
    raw_frame = {
    time: 1234,
    windDir360: [89,88],
    windSpeedKn: [12.1,12.4],
    boatSpeedKn: [6.5,6,6]
    };
    */
export function createEmptyRawFrame() {
    return {t:null, lat:[], lon:[], tws:[], twa:[], bs:[], sog:[], th: [], awa: [], aws: [], p: [], r: []};
}





function createEmptyTimeFrame(t, twa, tws, bs, lat, lon, sog, th, awa, aws, pitch, roll) {
    return { t: t, 
            twa: twa, 
            tws: tws, 
            bs: bs, 
            lat: lat, 
            lon: lon, 
            sog: sog, 
            th: th, 
            awa: awa, 
            aws: aws,
            p: pitch,
            r: roll
        };
}

function createTimeFrames(rawFrame, newTime) {
    const timeFrames = [];
    const maxItems = Math.max(rawFrame.tws.length,rawFrame.twa.length,rawFrame.bs.length, rawFrame.lat.length,rawFrame.lon.length, rawFrame.sog.length, rawFrame.th.length);
    const deltaMs = newTime.diff(rawFrame.t);
    for (let i = 0; i < maxItems; i++) {
        let frameTime = moment(rawFrame.t);
        frameTime.add(deltaMs/maxItems*i,'ms');
        let frame = createEmptyTimeFrame(
            frameTime,
            rawFrame.twa[Math.floor(i*rawFrame.twa.length/maxItems)],
            rawFrame.tws[Math.floor(i*rawFrame.tws.length/maxItems)],
            rawFrame.bs[Math.floor(i*rawFrame.bs.length/maxItems)],
            rawFrame.lat[Math.floor(i*rawFrame.lat.length/maxItems)],
            rawFrame.lon[Math.floor(i*rawFrame.lon.length/maxItems)],
            rawFrame.sog[Math.floor(i*rawFrame.sog.length/maxItems)],
            rawFrame.th[Math.floor(i*rawFrame.th.length/maxItems)],
            rawFrame.awa[Math.floor(i*rawFrame.awa.length/maxItems)],
            rawFrame.aws[Math.floor(i*rawFrame.aws.length/maxItems)],
            rawFrame.p[Math.floor(i*rawFrame.p.length/maxItems)],
            rawFrame.r[Math.floor(i*rawFrame.r.length/maxItems)]
            
        );

        
        timeFrames.push(frame);
    }
    // console.log("frame", timeFrames);
    return timeFrames
}


// function fixTimeJumps(timeFrames) {
//     let lastTime = null;
//     for(let i=0;timeFrames.length;i++) {
//         // time jumps forward 10 min.
//         if (lastTime != null && timeFrames[i].t.diff(lastTime) > 60*10*1000) {
//         timeFrames.splice(i, 1); // remove the frame
//         console.warn("Time jump at", lastTime.toISOString(), "to", timeFrames[i].t.toISOString());
//         i--;
//         } else {
//         lastTime = timeFrames[i].t;
//         }
//     }
//     return timeFrames;
// }
  
function removeImpossibleData(timeFrames) {
    let prevFr = null;
    for(let i=timeFrames.length-1; i >= 0 ; i--) {
        const f = timeFrames[i];
        let removeWind = false;
        if (f.tws > 100)
            removeWind = true;
        // if (prevFr != null && prevFr.tws > 8 && f.tws / prevFr.tws > 3)
        //     removeWind = true;
        // if (prevFr != null && f.tws - prevFr.tws > 15)
        //     removeWind = true;
        if (removeWind) {
            console.log("removed impossible wind value:", prevFr.t,prevFr.tws, f.t,f.tws);
            // console.log(JSON.stringify(prevFr),JSON.stringify(f));
            timeFrames.splice(i,1);
        } else {
            prevFr = f;
        }
    }
    return timeFrames;
}

function rollingAverage(timeFrames, resolutionSeconds = 1, windowSeconds=3) {
    let filteredTimeFrames = [];
    let start = 0;
    let outputTs = timeFrames[0].t.clone().add(resolutionSeconds, 'seconds').milliseconds(0);
    for(let i=0; i < timeFrames.length;i++) {
        let currentTs = timeFrames[i].t;
        if (currentTs.isSameOrAfter(outputTs)) {
            while (start < timeFrames.length && currentTs.diff(timeFrames[start].t, 'seconds') > windowSeconds) {
                start++;
            }
            const frames = timeFrames.slice(start, i + 1);
            filteredTimeFrames.push(
                createEmptyTimeFrame(
                    outputTs.clone(),
                    averageAngle(frames, f => f.twa),
                    averageValue(frames, 0, f => f.tws),
                    averageValue(frames, 2, f => f.bs),
                    averageValue(frames, null, f => f.lat),
                    averageValue(frames, null, f => f.lon),
                    averageValue(frames, 2, f => f.sog),
                    averageAngle(frames, f => f.th),
                    averageAngle(frames, f => f.awa),
                    averageValue(frames, 2, f => f.aws),
                    averageValue(frames, 2, f => f.p),
                    averageValue(frames, 2, f => f.r)
                )
            );
            outputTs.add(resolutionSeconds, 'second');       
        }
    }
    return filteredTimeFrames;
}

// function avgFrame(tf, start, end) {
    
// }

// function windowedAverage(timeFrames, windowSeconds=3) {
//     let latArr = [];
//     let lonArr = [];
//     let bsArr = [];
//     let twsArr = [];
//     let twaArr = [];
//     let thArr = [];
//     let sogArr = [];
//     let awaArr = [];
//     let awsArr = [];
//     let pArr = [];
//     let rArr = [];

//     let start = timeFrames[0].t;
//     let filteredTimeFrames = [];
//     for(const f of timeFrames) {
//         if (f.t.diff(start) > windowSeconds*1000) {
//             const ff = createEmptyTimeFrame(
//                 start,
//                 averageAngle(twaArr),
//                 averageValue(twsArr, 2),
//                 averageValue(bsArr, 2),
//                 averageValue(latArr),
//                 averageValue(lonArr),
//                 averageValue(sogArr, 2),
//                 averageValue(thArr, 2),
//                 averageAngle(awaArr),
//                 averageValue(awsArr, 2),
//                 averageValue(pArr, 2),
//                 averageValue(rArr, 2),

//             );
//             filteredTimeFrames.push(ff),
//             start = f.t;
//             // if (f.t.format("HHmmss") == "231402") {
//             //   console.log(f);
//             // }
//             latArr = [];
//             lonArr = [];
//             bsArr = [];
//             twsArr = [];
//             twaArr = [];
//             thArr = [];
//             sogArr = [];
//             awaArr = [];
//             awsArr = [];
//             pArr = [];
//             rArr = [];
//         }
//         pushDataToArray(latArr, f.lat, f, "latNaN");
//         pushDataToArray(lonArr, f.lon, f, "lonNaN");
//         pushDataToArray(bsArr, f.bs, f, "bsNaN");
//         pushDataToArray(twsArr, f.tws, f, "twsNaN");
//         pushDataToArray(twaArr, f.twa, f, "twaNaN");
//         pushDataToArray(thArr, f.th, f, "thNaN");
//         pushDataToArray(sogArr, f.sog, f, "sogNaN");
//         pushDataToArray(awaArr,f.awa,f,"awaNaN");
//         pushDataToArray(awsArr,f.aws,f,"awsNaN");
//         pushDataToArray(pArr,f.p,f,"pNaN");
//         pushDataToArray(rArr,f.r,f,"rNaN");
//     }
//     return filteredTimeFrames;
// }

// function pushDataToArray(arr, d, frame, msg) {
//     if (d != undefined && d != null &&  !isNaN(d)) {
//         arr.push(d);
//     } else {
//         // console.log(msg, frame);
//     }
// }


/**
 * Parsing functions for NMEA0183 sentences
 * See https://gpsd.gitlab.io/gpsd/NMEA.html
 * 
 * NMEA Encoding Conventions
Data is transmitted in serial async, 1 start-bit, 8 data-bits, 1 stop-bit, no parity. Data-bits are in least-significant-bit order. The standard specifies 4800 as the speed, but this is no longer common. The most-signifacant-bit is always zero.
An NMEA sentence consists of a start delimiter, followed by a comma-separated sequence of fields, followed by the character '*' (ASCII 42), the checksum and an end-of-line marker.
The start delimiter is normally '$' (ASCII 36). Packets of AIVDM/AIVDO data, which are otherwise formatted like NMEA, use '!'. Up to 4.00 these are the only permitted start characters [ANON].
The first field of a sentence is called the "tag" and normally consists of a two-letter talker ID followed by a three-letter type code.
Where a numeric latitude or longitude is given, the two digits immediately to the left of the decimal point are whole minutes, to the right are decimals of minutes, and the remaining digits to the left of the whole minutes are whole degrees.
Eg. 4533.35 is 45 degrees and 33.35 minutes. ".35" of a minute is exactly 21 seconds.
In NMEA 3.01 (and possibly some earlier versions), the character "^" (HEX 5E) is reserved as an introducer for two-character hex escapes using 0-9 and A-F, expressing an ISO 8859-1 (Latin-1) character [ANON].
The Checksum is mandatory, and the last field in a sentence. It is the 8-bit XOR of all characters in the sentence, excluding the "$", "I", or "*" characters; but including all "," and "^". It is encoded as two hexadecimal characters (0-9, A-F), the most-significant-nibble being sent first.
Sentences are terminated by a <CR><LF> sequence.
Maximum sentence length, including the $ and <CR><LF> is 82 bytes.
According to [UNMEA], the NMEA standard requires that a field (such as altitude, latitude, or longitude) must be left empty when the GPS has no valid data for it. However, many receivers violate this. It’s common, for example, to see latitude/longitude/altitude figures filled with zeros when the GPS has no valid data.

 */
//$IIVHW,232.2,T,255.5,M,6.80,N,12.59,K*62
const NMEA_VHW = /^\$..VHW/;
export function storeSpeedFromVHW(line, frame) {
    if (line.match(NMEA_VHW)) {
        const fields = line.split('*')[0].split(',');
        let bs = parseFloat(fields[5]);
        if (!isNaN(bs)) {
            if (fields[6] == 'K')
                bs = bs * 1.85200426;
            frame.bs.push(bs);
            //  console.log("bs:"+bs);
        }
        if (fields[2] == 'T') {
            const th = parseFloat(fields[1]);
            if (!isNaN(th)) {
                frame.th.push(th);
            }
        }
    }
}

//'$IIXDR,A,1.4,D,PTCH,A,4.9,D,ROLL*54\r'
function storeOrientationFromXDR(line, frame) {
    if (line.startsWith('$IIXDR') || line.startsWith('$GPXDR') || line.startsWith('$GNXDR')) {
        const fields = line.split('*')[0].split(',');

        // Loop through fields in chunks of 4 (type, value, unit, label)
        for (let i = 1; i < fields.length - 3; i += 4) {
            const value = parseFloat(fields[i + 1]);
            const unit = fields[i + 2];
            const label = fields[i + 3];

            if (unit === 'D') { // Only parse if unit is degrees
                if (label === 'PTCH') {
                frame.p.push(parseFloat(value));
                } else if (label === 'ROLL') {
                frame.r.push(parseFloat(value));
                }
            }
        }
    }
}

//$IIMWV,336.86,T,14.12,M,A*06
//$IIMWV,340.50,R,17.49,M,A*07
const NMEA_MWV = /^\$..MWV/;
export function storeWindFromMWV(line, frame) {
    if (line.match(NMEA_MWV)) {
        //const match = line.match(/\$..MWV,([0-9.]+),T,([0-9.]+),M/);
        const fields = line.split('*')[0].split(',');
        const windAngle = parseFloat(fields[1]);
        if (!isNaN(windAngle)) {
            if (fields[2] == 'T') {
                frame.twa.push(windAngle);
            } else {
                frame.awa.push(windAngle);
            }
        }
        let windSpeed = parseFloat(fields[3]);
        if (!isNaN(windSpeed)) {
            if (fields[4] == 'M') windSpeed *= 1.943844;
            if (fields[4] == 'K') windSpeed *= 0.53996;
            windSpeed = parseFloat(windSpeed.toFixed(2));
            if (fields[2] == 'T') {
                frame.tws.push(windSpeed);
            } else {
                frame.aws.push(windSpeed);
            }
        }
        // if (match) {
        // const twa = parseFloat(parseFloat(match[1]).toFixed(2));
        // if (!isNaN(twa))
        //     frame.twa.push(twa);
        // const tws = (parseFloat(parseFloat(match[2]) * 1.943844).toFixed(2));
        // if (!isNaN(tws))  
        //     frame.tws.push(parseFloat(tws));
        // }
    }
}

const NMEA_VWR = /\$..VWR,([0-9.]+),(L|R),([0-9.]+),N/;
export function storeApparentWindFromVWR(line, frame) {
    const match = line.match(NMEA_VWR);
    if (match) {
        let awa = parseFloat(match[1]);
        const awaDir = match[2];
        if (awaDir != null && !isNaN(awa)) {
            awa = awaDir == "L" ? 360 - awa : awa;
            frame.awa.push(awa);
        }
        const aws = parseFloat(match[3]);
        if (!isNaN(aws))  
            frame.aws.push(aws);
    }
}



//stores as millis since epoch time string, to parse use moment(ts)
// this code has a midnight bug

export function storePosition(line, frame, oldTime) {
    storePositionFromGPGGA(line, frame);
    const parsedTime = storePositionFromRMC(line, frame);
    if (parsedTime != null) {
        if (!isTimeJump(parsedTime, oldTime)) { // time bug in data
            return parsedTime;
        }
    }
    return null;
}

// function storePosition(line, frame, oldTime,referenceDate, timeSentence) {
//     let parsedTime = null;
//     const ggaTime = storePositionFromGPGGA(line, frame);
//     const rmcTime = storePositionFromRMC(line, frame);
//     if (timeSentence != null){
//         parsedTime = timeSentence == NMEA_GGA_SENT ? ggaTime : rmcTime;
//     } else {
//         parsedTime = rmcTime == null ? ggaTime : rmcTime;
//     }

//     if (parsedTime != null && referenceDate != null) {
//         correctTime(parsedTime, referenceDate, oldTime);
//         if (!isTimeJump(parsedTime, oldTime)) { // time bug in data
//         return parsedTime;
//         }
//     }
//     return null;
// }

// function correctTime(newTime, referenceDate, oldTime) {
//     newTime.year(referenceDate.year()).month(referenceDate.month()).date(referenceDate.date());
//     if (oldTime != null && oldTime.diff(newTime) > 23*60*60*1000) { // fix when time jumps back nearly 24 hr
//         referenceDate.add(1,'d');
//         newTime.add(1,'d');
//     }
//     }

function isTimeJump(newTime, oldTime) {
    // detect 10 min time jump
    //if (newTime != null && oldTime != null && (newTime.isBefore(oldTime) || newTime.diff(oldTime) > 60 * 10 * 1000)) {
    if (newTime != null && oldTime != null && (newTime.valueOf() + 1000 < oldTime.valueOf() || newTime.valueOf() - oldTime.valueOf() > 60 * 10 * 1000)) {
        console.warn("Time jump at", oldTime.toISOString(), "to", newTime.toISOString());
        return true;
    }
    return false;
}

export function validateChecksum(line) {
    if (line.startsWith("$STALK"))
        return true;
    const parts = line.split('*');
    if (parts.length == 1)
        return true;
    
    if (parts.length !== 2 || parts[1].length < 2) {
        console.warn("Couldn't match checksum content:",line);
        return false;
    }
    
    let checksum = 0;
    const content = parts[0];
    for (let i = 1; i < content.length; i++) {
        checksum ^= content.charCodeAt(i);
    }
    
    const computed = checksum.toString(16).toUpperCase().padStart(2, '0');
    const nmeaChecksum = parts[1].substring(0, 2).toUpperCase();
    
    if (computed !== nmeaChecksum) {
        console.warn(`Checksum expected ${nmeaChecksum}, got ${computed}, in [${line}]`);
        return false;
    }
    
    return true;
}

//$GPGGA,013020,4119.0358,S,17450.3759,E,2,17,0.6,-9.0,M,0.0,M,,*44
const NMEA_GGA = /^\$[A-Z][A-Z]GGA/;
function storePositionFromGPGGA(line, frame) {
    let parsedTime = null;
    if (line.match(NMEA_GGA)) {
        const fields = line.split(',');

        // Parse time
        const t = fields[1];
        if (t) {
        parsedTime = moment.utc(t, "HHmmss.SS");
        }

        // Parse latitude
        const lat = fields[2];
        const latDir = fields[3];
        if (lat && latDir) {
        const degrees = parseInt(lat.slice(0, 2), 10);
        const minutes = parseFloat(lat.slice(2));
        let latitude = degrees + minutes / 60;
        if (latDir === 'S') latitude *= -1;
        frame.lat.push(latitude);
        }

        // Parse longitude
        const lon = fields[4];
        const lonDir = fields[5];
        if (lon && lonDir) {
        const degrees = parseInt(lon.slice(0, 3), 10);
        const minutes = parseFloat(lon.slice(3));
        let longitude = degrees + minutes / 60;
        if (lonDir === 'W') longitude *= -1;
        frame.lon.push(longitude);
        }
    }
    return parsedTime;
}


//$GNRMC,012959.000,A,4119.0629,S,17450.4077,E,5.87,321.90,060425,,,D,V*11
const NMEA_RMC = /^\$[A-Z][A-Z]RMC/;
export function storePositionFromRMC(line, frame) {
    let parsedTime = null;
    if (line.match(NMEA_RMC)) {
        const fields = line.split(',');

        // Parse time
        const t = fields[1];
        const date = fields[9];
        parsedTime = moment.utc(`${date}${t}`, "DDMMYYHHmmss.SS");

        // Parse latitude
        const lat = fields[3];
        const latDir = fields[4];
        if (lat && latDir) {
        const degrees = parseInt(lat.slice(0, 2), 10);
        const minutes = parseFloat(lat.slice(2));
        let latitude = degrees + minutes / 60;
        if (latDir === 'S') latitude *= -1;
        frame.lat.push(latitude);
        }

        // Parse longitude
        const lon = fields[5];
        const lonDir = fields[6];
        if (lon && lonDir) {
        const degrees = parseInt(lon.slice(0, 3), 10);
        const minutes = parseFloat(lon.slice(3));
        let longitude = degrees + minutes / 60;
        if (lonDir === 'W') longitude *= -1;
        frame.lon.push(longitude);
        }

        // Parse speed over ground (in knots)
        const sog = fields[7];
        if (sog) {
        frame.sog.push(parseFloat(sog)); 
        }
    }

    return parsedTime;
}

//$IIZDA,013023.020,06,04,2025,,*47
// const NMEA_ZDA = /^\$[A-Z][A-Z]ZDA/;
// function referenceDateFromZDA(line, referenceDate) {
//     if (line.match(NMEA_ZDA)) {
//         const fields = line.split(',');

//         const timeStr = fields[1];  // hhmmss.ss
//         const dayStr = fields[2];   // dd
//         const monthStr = fields[3]; // mm
//         const yearStr = fields[4];  // yyyy

//         if (timeStr && dayStr && monthStr && yearStr) {
//         const dateTimeStr = `${yearStr}-${monthStr}-${dayStr}T${timeStr.slice(0,2)}:${timeStr.slice(2,4)}:${timeStr.slice(4)}Z`;
//         const parsedDate = moment.utc(dateTimeStr);
//         if (parsedDate.isBefore(referenceDate) || parsedDate.diff(referenceDate, 'days') > 1) {
//             console.warn("ZDA reference data looks suspicious, rejecting. Current:", referenceDate.toISOString(), "candidate:", parsedDate.toISOString());
//         } else {
//             return parsedDate;
//         }
//         }
//     }
//     return referenceDate;
// }

//$IIHDT,321.3,T*21
const NMEA_HDT = /^\$..HDT/;
export function storeHeadingFromHDT(line, frame) {
    if (line.match(NMEA_HDT)) {
        const fields = line.split('*')[0].split(',');

        const headingStr = fields[1];
        const type = fields[2];

        if (headingStr && type === 'T') {
            const heading = parseFloat(headingStr);
            if (!isNaN(heading)) {
                frame.th.push(heading);
            }
        }
    }
}

/*
https://gpsd.gitlab.io/gpsd/AIVDM.html
https://gpsd.gitlab.io/gpsd/
https://gitlab.com/gpsd/gpsd/-/blob/master/devtools/ais.py
*/

/*function storeAisSentence(line, aisTracks, currentTime) {
    const result = parseAisSentence(line);
    if (result != null && currentTime != null) {
    result.utcTime = currentTime;
    const mmsi = result.mmsi;
    if (!aisTracks[mmsi]) {
        aisTracks[mmsi] = { mmsi: mmsi, shipName:null, data: [] };
    }
    if (result.msgType == 24 && result.shipName != null)
        aisTracks[mmsi].shipName = result.shipName;
    else if (result.msgType == 18) {
        const data = {t: currentTime, lat: result.latitude, lon: result.longitude, sog: result.speedOverGround};
        if (result.trueHeading && result.trueHeading <= 360) {
        data.th = result.trueHeading;
        }
        aisTracks[mmsi].data.push(data);
    }
    }
}*/

    function saveAisResult(result, aisTracks) {
    const mmsi = result.mmsi;
    if (!aisTracks[mmsi]) {
        aisTracks[mmsi] = { mmsi: mmsi, shipName: null, data: [] };
    }
    if (result.shipName != null) {
        aisTracks[mmsi].shipName = result.shipName;
        aisTracks[mmsi].callsign = result.callsign != null ? result.callsign : '';
    } else if (result.latitude != null) {
        const data = { t: result.utcTime, lat: result.latitude, lon: result.longitude, sog: result.speedOverGround, cog: result.courseOverGround };
        if (result.trueHeading && result.trueHeading <= 360) {
            data.th = result.trueHeading;
        }
        aisTracks[mmsi].data.push(data);
    }
}

let lastAisFragment = null;

function storeAisSentence(line, aisTracks, currentTime) {
    const parts = line.split(',');
    if (parts.length < 5 || !parts[0].startsWith('!AIVDM'))
        return;

    const totalFragments = parseInt(parts[1], 10);
    const fragmentNumber = parseInt(parts[2], 10);
    const payload = parts[5];

    if (totalFragments === 2) {
        if (fragmentNumber === 1) {
            // First part: store it
            lastAisFragment = {
                parts: [payload],
                originalLine: parts.slice(0) // Clone the parts array
            };
        } else if (fragmentNumber === 2 && lastAisFragment) {
            // Second part: combine and parse
            const combinedPayload = lastAisFragment.parts[0] + payload;

            // Assemble a full NMEA line
            const mergedParts = lastAisFragment.originalLine;
            mergedParts[5] = combinedPayload;
            const fillBits = mergedParts[6].split('*')[0]; // Use fill bits from part 2
            mergedParts[6] = fillBits + mergedParts[6].slice(fillBits.length); // keep checksum
            const mergedLine = mergedParts.join(',');

            const result = parseAisSentence(mergedLine);
            lastAisFragment = null; // Clear

            if (result != null && currentTime != null) {
                result.utcTime = currentTime;
                saveAisResult(result, aisTracks);
            }
        } else {
            console.warn('Unexpected AIS fragment without first part.');
        }
    } else {
        // Normal single-part message
        const result = parseAisSentence(line);
        if (result != null && currentTime != null) {
            result.utcTime = currentTime;
            saveAisResult(result, aisTracks);
        }
    }
}



function summarizeAisTracks(aisTracks) {
    let lines = "";
    for(const mmsi in aisTracks) {
    const track = aisTracks[mmsi];
    lines += `mmsi:${mmsi}, shipName:${track.shipName}, positions:${track.positions.length}\n`;
    }
    return lines;
}

// useful info at 
// https://gpsd.gitlab.io/gpsd/AIVDM.html
function parseAisSentence(nmea) {
    function sixbitToBinary(payload) {
        let bin = '';
        for (let char of payload) {
            let val = char.charCodeAt(0) - 48;
            if (val > 40) val -= 8;
            bin += val.toString(2).padStart(6, '0');
        }
        return bin;
    }

    function getInt(bin, start, length) {
        return parseInt(bin.slice(start, start + length), 2);
    }

    function getSignedInt(bin, start, length) {
        const val = getInt(bin, start, length);
        const signBit = 1 << (length - 1);
        return (val & signBit) ? val - (1 << length) : val;
    }

    function decodeSixBitStr(bin, start, length) {
        let str = '';
        for (let i = 0; i < length; i += 6) {
            const val = parseInt(bin.slice(start + i, start + i + 6), 2);
            const char = val < 32 ? String.fromCharCode(val + 64) : String.fromCharCode(val);
            str += char;
        }
        return str.trim().replace(/@+$/, '');
    }

    if (!nmea.startsWith('!AIVDM') && !nmea.startsWith('!AIVDO')) {
        // throw new Error('Not a valid AIS NMEA sentence');
        return null;
    }

    const parts = nmea.split(',');
    const payload = parts[5];
    const fillBitsStr = parts[6].split('*')[0];
    const fillBits = parseInt(fillBitsStr, 10);
    const bin = sixbitToBinary(payload);
    const binPayload = bin.slice(0, bin.length - fillBits);

    const msgType = getInt(binPayload, 0, 6);
    const mmsi = getInt(binPayload, 8, 30);
    const result = { msgType, mmsi };

    if (msgType === 18) {
        result.speedOverGround = getInt(binPayload, 46, 10) / 10;
        result.longitude = getSignedInt(binPayload, 57, 28) / 600000;
        result.latitude = getSignedInt(binPayload, 85, 27) / 600000;
        result.courseOverGround = getInt(binPayload, 112, 12) / 10;
        result.trueHeading = getInt(binPayload, 124, 9);
        result.timestamp = getInt(binPayload, 133, 6);
    //   result.assigned = getInt(binPayload, 146, 1);
    } else if (msgType === 24) {
        result.partNumber = getInt(binPayload, 38, 2);
        if (result.partNumber === 0) {
            result.shipName = decodeSixBitStr(binPayload, 40, 120);
        } else if (result.partNumber === 1) {
        //   result.vendorId = decodeSixBitStr(binPayload, 48, 42);
            result.callsign = decodeSixBitStr(binPayload, 90, 42);
        }
    } else if(msgType === 1 || msgType === 2 || msgType === 3) {
        result.speedOverGround = getInt(binPayload, 50, 10) / 10;
        result.longitude = getSignedInt(binPayload, 61, 28) / 600000;
        result.latitude = getSignedInt(binPayload, 89, 27) / 600000;
        result.courseOverGround = getInt(binPayload, 116, 12) / 10;
        result.trueHeading = getInt(binPayload, 128, 9);
        result.timestamp = getInt(binPayload, 137, 6);
    } else if (msgType === 5) {
    result.callsign = decodeSixBitStr(binPayload, 70, 42);
    result.shipName = decodeSixBitStr(binPayload, 112, 120);
} else {
    //   result.error = `Unsupported AIS message type: ${msgType}`;
    // console.log("un parsed Message:"+JSON.stringify(result));
    return null;
    }
//   console.log("parsedAisMessage:"+JSON.stringify(result));
    return result;
}
  
  
/*
Need to decide which sentances to use.
Halo uses
 - th: HDG
 - bs: VHW
 - true wind: n/a
 - app wind: MWV
 - orientation: n/a
 - gps pos: RMC
 - time: RMC

 Siver uses:
 - th: HDT (obsolete) | VHW
 - bs: VHW
 - true wind: MWV
 - app wind: VWR | MWV
 - orientation: XDR
 - gps pos: RMC (stop using GGA)
 - time: RMC


*/

function dataFunctionList(lines) {
    lines = lines.slice(0,1000);
    const functions = [];
    const fVWR = testParse(storeApparentWindFromVWR, lines);
    const fMWV = testParse(storeWindFromMWV, lines);
    if (fMWV.awa.length == 0 && fVWR.awa.length > 0) {
        //TODO this branch seems to never be called and fn does not exist. validate this/fix
        functions.push(storeWindFromVWR);
        console.log('Using VWR for apparent wind as MWV does not contain it');
    }
    if (fMWV.twa.length == 0) {
        console.log('Data set does not contain TWS or TWA !!');
    }
    
    const fHDT = testParse(storeHeadingFromHDT, lines);
    const fVHW = testParse(storeSpeedFromVHW, lines);
    if (fVHW.th.length < 10 && fHDT.th.length > 0) {
        functions.push(storeHeadingFromHDT);
        console.log('Using HDT for apparent wind as VHW does not contain it');
    }

    return functions;
}

function testParse(fn, lines) {
    const frame = createEmptyRawFrame();
    for(const line of lines) {
        fn(line,frame);
    }
    return frame;
}

/**
 * Creates a new frame object for insertion into a track.
 * @param {moment|number} timestamp - Frame timestamp (moment object or unix seconds)
 * @param {number} lat - Latitude
 * @param {number} lon - Longitude
 * @returns {Object} Frame object with t, lat, lon fields
 */
export function createFrame(timestamp, lat, lon) {
    const t = typeof timestamp === 'number' 
        ? moment.unix(timestamp)
        : timestamp;
    
    return {
        t: t,
        lat: lat,
        lon: lon,
        sog: 0,
        bs: 0
    };
}
